import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  addEdge,
  Background,
  ReactFlow,
  BackgroundVariant,
  Controls,
  MiniMap,
  applyNodeChanges,
  applyEdgeChanges,
  MarkerType,
  Node,
  Edge,
  SelectionMode,
  useReactFlow,
} from "@xyflow/react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { usePageHeading } from "@/helper/customHooks/usePageHeading";
import { api } from "@/helper/api";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import styles from "@/components/actionChain/actionChains.module.css";
import "@xyflow/react/dist/style.css";
import ActionChainCustomNode from "@/components/actionChain/actionChainCustomNode";
import ActionChainCustomConnection from "@/components/actionChain/actionChainCustomConnection";
import ActionChainModalContent from "@/components/actionChain/actionChainModalContent";
import { useErrorModal } from "@/components/forms/errorHandling/useErrorModal";
import { v4 as uuidv4 } from "uuid";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import { useConfirmationModal } from "@/context/confirmationModalContext";
import GenericNotification from "@/components/notification/genericNotification";
import ActionChainActions from "@/components/actionChain/actionChainActions";
import useConfirmOnLeave from "@/helper/customHooks/useConfirmOnLeave";
import { useRegisterUnsavedChanges } from "@/context/unsavedChangesContext";
import useFocusFirstInvalidField from "@/helper/customHooks/useFocusFirstInvalidField";

type User = {
  id: string;
  name: string;
};

type ActionChainTemplate = { id: string; name: string } | string;

type ActionChainItem = {
  id: string;
  created_by: User;
  modified_by: User;
  created_on: string;
  modified_on: string;
  metadata: any;
  deleted: boolean;
  enabled: boolean;
  name: string;
  action: string;
  additional_data: any;
  action_chain_template: ActionChainTemplate;
  previous_items_or: any[];
  previous_items_and: any[];
  is_entrypoint?: boolean;
};

type ActionKeys =
  | "create_todo"
  | "generate_pdf"
  | "start_action_chain"
  | "send_email"
  | "mark_completed"
  | "validate_condition"
  | "create_tracked_service"
  | "create_note";

export const actionParameterMap: Record<ActionKeys, any> = {
  create_todo: {
    done: false,
    name: "TITLE",
    type: "text",
    target: {
      link: "URL",
      linkTitle: "LINK_TITLE",
    },
    description: "DESCRIPTION",
    due_date_in_days: 28,
    create_for: "subject",
  },

  generate_pdf: {
    filename_template: "SOME_CUSTOM_FILENAME_{{ entity.subject.name }}",
    template: "TEMPLATE_NAME",
    custom_vars: {},
  },
  start_action_chain: { id: "ACTION_CHAIN_TEMPLATE" },
  send_email: { mail_template_name: "MAIL_TEMPLATE_NAME", additional_tags: {} },
  create_tracked_service: {
    tracked_service_template_id: "TRACKED_SERVICE_TEMPLATE_ID",
  },

  mark_completed: {},

  validate_condition: {
    conditions: [{ key: "{{  }}", value: "", type: "is" }],
  },
  create_note: {
    text: "The service level for this company is 'flatrate', and the company name is not 'kaer'",
  },
};

const SUBJECT_CREATE_FOR_VALUE = "subject";

function stripDefaultCreateFor(actionValue: string, additionalData: any) {
  if (
    actionValue === "create_todo" &&
    additionalData &&
    typeof additionalData === "object" &&
    additionalData.create_for === SUBJECT_CREATE_FOR_VALUE
  ) {
    const rest = { ...additionalData };
    delete rest.create_for;
    return rest;
  }
  return additionalData;
}

export interface CustomNode extends Node {
  actionChainItem: ActionChainItem;
}
export interface CustomEdge<T = any> extends Edge {
  customData?: T;
}

function getActionChainGraphSignature(
  nodes: CustomNode[],
  edges: CustomEdge[]
): string {
  const normalizedNodes = nodes
    .map((node) => ({
      id: node.id,
      x: node.position?.x,
      y: node.position?.y,
      name: node.actionChainItem?.name ?? (node.data as any)?.label,
      action: node.actionChainItem?.action,
      additional_data: node.actionChainItem?.additional_data,
      submitMethod: node.actionChainItem?.metadata?.submitMethod,
      previous_items_and: (node.actionChainItem?.previous_items_and ?? []).map(
        (item: any) => item?.id ?? item
      ),
      previous_items_or: (node.actionChainItem?.previous_items_or ?? []).map(
        (item: any) => item?.id ?? item
      ),
    }))
    .sort((a, b) => String(a.id).localeCompare(String(b.id)));

  const normalizedEdges = edges
    .map((edge) => ({ source: edge.source, target: edge.target }))
    .sort((a, b) =>
      `${a.source}->${a.target}`.localeCompare(`${b.source}->${b.target}`)
    );

  return JSON.stringify({ nodes: normalizedNodes, edges: normalizedEdges });
}

const ActionChainCreateAndEdit: FC = () => {
  const [action, setAction] = useState<CustomNode[]>([]);
  const [connection, setConnection] = useState<CustomEdge[]>([]);
  const [actionNameModalOpen, setActionNameModalOpen] =
    useState<boolean>(false);
  const [actionOptions, setActionOptions] = useState<any[]>([]);
  const [actionChainTemplateName, setActionChainTemplateName] =
    useState<string>("");
  const [lastActionNodeY, setLastActionNodeY] = useState<number>(0);
  const [positionOnAddingAction, setPositionOnAddingAction] =
    useState<any>(null);
  const [modalMode, setModalMode] = useState<"create" | "edit">("create");
  const [refreshTrigger, setRefreshtrigger] = useState<boolean>(false);
  const { handleError, errorModal } = useErrorModal();
  const [successForNotification, setSuccessForNotification] =
    useState<boolean>(false);
  const [notificationMessage, setNotificationMessage] = useState<any>("");
  const [notificationVariant, setNotificationVariant] = useState<
    "success" | "error" | "warning" | "info" | undefined
  >();
  const [selection, setSelection] = useState<boolean>(false);
  const [selectedNodes, setSelectedNodes] = useState<any[]>([]);

  const { showConfirmationModalAsync } = useConfirmationModal();
  const { screenToFlowPosition } = useReactFlow();

  const { t } = useTranslation();
  usePageHeading(actionChainTemplateName || t("actionchains"));
  const navigate = useNavigate();
  const { actionChainId } = useParams() as {
    actionChainId: string;
  };
  const methods = useForm<any>({
    shouldFocusError: false,
    defaultValues: {
      name: "",
      action: { value: "", label: "" },
      additional_data: {},
    },
  });
  const { registerValidationTarget, focusFirstInvalidField } =
    useFocusFirstInvalidField<
      Record<"name" | "action" | "additional_data", any>
    >({
      fieldOrder: ["name", "action", "additional_data"],
      focusableSelector:
        'input:not([type="hidden"]), textarea, select, [role="combobox"]',
    });

  const baselineSignatureRef = useRef<string>(
    getActionChainGraphSignature([], [])
  );

  const hasUnsavedChanges = useMemo(
    () =>
      getActionChainGraphSignature(action, connection) !==
      baselineSignatureRef.current,
    [action, connection]
  );

  useConfirmOnLeave(hasUnsavedChanges, "unsaved_formChanges_confirm");
  useRegisterUnsavedChanges("ActionChainCreateAndEdit", hasUnsavedChanges);

  const updateSavedBaseline = useCallback(
    (nodes: CustomNode[], edges: CustomEdge[]) => {
      baselineSignatureRef.current = getActionChainGraphSignature(nodes, edges);
    },
    []
  );

  const nodesWithDeleteHandlerWidth = "300px";
  const nodesWithDeleteHandlerHeight = "95px";
  const companyProgressNodeWidth = "3000px";
  const companyProgressNodeHeight = "";

  const actionValue = methods.watch("action") as { value: ActionKeys } | null;

  const prevActionRef = useRef<ActionKeys | null>(null);

  const isConnectingRef = useRef<boolean>(false);

  useEffect(() => {
    if (typeof actionValue === "object" && actionValue !== null) {
      const additionalParams = actionParameterMap[actionValue.value];
      if (modalMode === "create") {
        methods.setValue("additional_data", additionalParams);
      }
      if (modalMode === "edit") {
        if (additionalParams && actionValue.value !== prevActionRef.current) {
          methods.setValue("additional_data", additionalParams);
          prevActionRef.current = actionValue.value;
        }
      }
    }
  }, [actionValue, modalMode]);

  useEffect(() => {
    if (actionChainId != null) {
      getActionChainTemplateItemsById(
        actionChainId,
        action,
        setAction,
        setConnection,
        setLastActionNodeY,
        handleError,
        updateSavedBaseline
      );
      getActionChain(actionChainId, setActionChainTemplateName, handleError);
    }
  }, [actionChainId, refreshTrigger]);

  const openModal = (position: any) => {
    if (selection || isConnectingRef.current) {
      return;
    }
    const flowPosition = screenToFlowPosition({
      x: position.clientX,
      y: position.clientY,
    });
    setPositionOnAddingAction(flowPosition);
    methods.reset();
    setModalMode("create");
    setActionNameModalOpen(true);
  };

  const closeModal = () => {
    setActionNameModalOpen(false);
  };

  useEffect(() => {
    api.genericApiRequest({
      method: "get",
      entity: "actionChainItemTemplate",
      additionalRouteParts: { available_actions: "/" },
      successHandler: (res: any) => {
        const actions = res.data
          .filter((action: any) => action !== "mark_completed")
          .map((action: any) => ({
            value: action,
            label: action,
          }));
        setActionOptions(actions);
      },
      failHandler: "",
    });
  }, []);

  const onActionChange = useCallback(
    (changes: any) => setAction((nds) => applyNodeChanges(changes, nds)),
    []
  );
  const onConnectionChange = useCallback(
    (changes: any) => setConnection((eds) => applyEdgeChanges(changes, eds)),
    []
  );

  const onConnectStart = useCallback(() => {
    isConnectingRef.current = true;
  }, []);

  const onConnectEnd = useCallback(() => {
    setTimeout(() => {
      isConnectingRef.current = false;
    }, 0);
  }, []);

  const onAddConnection = useCallback(
    (params: any) => {
      const sourceNode = action.find((node) => node.id === params.source);
      const targetNode = action.find((node) => node.id === params.target);

      const sourceNodeData = sourceNode?.data || {};
      const targetNodeData = targetNode?.data || {};

      const updatedActions = action.map((node: any) => {
        if (node.id === params.target) {
          return {
            ...node,
            actionChainItem: {
              ...node.actionChainItem,
              previous_items_and: [
                ...(node.actionChainItem.previous_items_and || []),
                { id: params.source, label: sourceNodeData.label },
              ],
            },
          };
        }
        return node;
      });

      setConnection((eds) =>
        addEdge(
          {
            ...params,
            type: "withDeleteHandler",
            markerEnd: {
              type: MarkerType.ArrowClosed,
            },
            data: {
              sourceData: sourceNodeData,
              targetData: targetNodeData,
            },
          },
          eds
        )
      );
      setAction(updatedActions);
    },
    [action]
  );

  const onDeleteConnection = useCallback(
    async (event: React.MouseEvent, id: string) => {
      event.stopPropagation();

      const confirmDelete = await showConfirmationModalAsync(
        t("actionchain_confirm_delete_connection")
      );

      if (!confirmDelete) return;

      setConnection((connection: any) => {
        const connectionToDelete = connection.find((e: any) => e.id === id);

        if (connectionToDelete) {
          const { source, target } = connectionToDelete;

          setAction((prevActions) =>
            prevActions.map((action: any) => {
              if (action.id === target) {
                return {
                  ...action,
                  actionChainItem: {
                    ...action.actionChainItem,
                    previous_items_and: (
                      action.actionChainItem.previous_items_and || []
                    ).filter((item: any) => item.id !== source),
                  },
                };
              }
              return action;
            })
          );
        }

        return connection.filter((e: any) => e.id !== id);
      });
    },
    []
  );

  const handleDeleteNodes = useCallback(
    async (nodesToDelete: string | string[]) => {
      const confirmDelete = await showConfirmationModalAsync(
        t(
          Array.isArray(nodesToDelete)
            ? "actionchain_bulk_delete_confirm"
            : "actionchain_confirm_delete_action"
        )
      );

      if (!confirmDelete) return;

      const nodesArray = Array.isArray(nodesToDelete)
        ? nodesToDelete
        : [nodesToDelete];

      setAction((nds) =>
        nds.map((node: any) =>
          nodesArray.includes(node.id)
            ? {
                ...node,
                actionChainItem: {
                  ...node.actionChainItem,
                  metadata: {
                    ...node.actionChainItem.metadata,
                    submitMethod: "delete",
                    deleted: true,
                  },
                },
              }
            : node
        )
      );

      setConnection((eds) =>
        eds.filter(
          (edge) =>
            !nodesArray.includes(edge.source) &&
            !nodesArray.includes(edge.target)
        )
      );
    },
    [setAction, setConnection]
  );

  const removeDeletedNodesFromState = (deletedNodeIds: string[]) => {
    setAction((prevNodes) =>
      prevNodes.filter((node) => !deletedNodeIds.includes(node.id))
    );
    setConnection((prevEdges) =>
      prevEdges.filter(
        (edge) =>
          !deletedNodeIds.includes(edge.source) &&
          !deletedNodeIds.includes(edge.target)
      )
    );
  };
  //Copy/paste selectedNodes
  const copyAndPasteNodes = useCallback(
    (selectedNodes: any) => {
      if (!selectedNodes || selectedNodes.length === 0) return;

      const idMap = new Map();
      selectedNodes.forEach((node: any) => {
        idMap.set(node.id, uuidv4());
      });

      const copied = selectedNodes.map((node: any) => {
        const newId = idMap.get(node.id);

        return {
          ...node,
          id: newId,
          position: {
            x: node.position.x + 50,
            y: node.position.y + 50,
          },
          data: {
            ...node.data,
            id: newId,
            label: node.data.label,
            previous_items_and: node.data.previous_items_and
              ?.filter((prev: any) => idMap.has(prev.id))
              ?.map((prev: any) => ({
                ...prev,
                id: idMap.get(prev.id),
              })),
            previous_items_or: node.data.previous_items_or
              ?.filter((prev: any) => idMap.has(prev.id))
              ?.map((prev: any) => ({
                ...prev,
                id: idMap.get(prev.id),
              })),
          },
          actionChainItem: {
            ...node.actionChainItem,
            name: node.data.label,
            submitMethod: "post",
            metadata: {
              position: {
                x: node.position.x + 50,
                y: node.position.y + 50,
              },
              submitMethod: "post",
            },
            previous_items_and: node.actionChainItem.previous_items_and
              ?.filter((prev: any) => idMap.has(prev.id))
              ?.map((prev: any) => ({
                ...prev,
                id: idMap.get(prev.id),
              })),
            previous_items_or: node.actionChainItem.previous_items_or
              ?.filter((prev: any) => idMap.has(prev.id))
              ?.map((prev: any) => ({
                ...prev,
                id: idMap.get(prev.id),
              })),
          },
        };
      });

      setAction((prevActions: any) =>
        prevActions
          .map((node: any) => ({
            ...node,
            selected: false,
          }))
          .concat(copied.map((node: any) => ({ ...node, selected: true })))
      );
      const newEdges = copied.flatMap((node: any) =>
        node.actionChainItem.previous_items_and
          ?.map((prevItem: any) => {
            if (!Array.from(idMap.values()).includes(prevItem.id)) return null;

            return {
              id: `e${prevItem.id}-${node.id}`,
              source: prevItem.id,
              target: node.id,
              type: "withDeleteHandler",
              markerEnd: { type: MarkerType.ArrowClosed },
              data: { sourceData: prevItem, targetData: node.data },
            };
          })
          .filter(Boolean)
      );

      setConnection((prevEdges: any) => [...prevEdges, ...newEdges]);
    },
    [setAction, setConnection]
  );

  //Handle keyInputs (Ctrl+C & Ctrl+V)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.key.toLowerCase() === "c") {
        event.preventDefault();
        copyAndPasteNodes(selectedNodes);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [copyAndPasteNodes, selectedNodes]);

  const handleModalOpenOnNodeEdit = (
    open: boolean,
    id: string,
    { label, action, additional_data }: any
  ) => {
    setModalMode("edit");

    // create_todo items created with "subject" have create_for stripped on save, so
    // re-inject the default here to keep the "erstellen für" dropdown populated.
    let additionalDataForForm = additional_data;
    if (
      action === "create_todo" &&
      (!additionalDataForForm ||
        typeof additionalDataForForm !== "object" ||
        additionalDataForForm.create_for == null)
    ) {
      additionalDataForForm = {
        ...(additionalDataForForm && typeof additionalDataForForm === "object"
          ? additionalDataForForm
          : {}),
        create_for: SUBJECT_CREATE_FOR_VALUE,
      };
    }

    methods.setValue("name", label);
    methods.setValue("action", action);
    prevActionRef.current = action;
    methods.setValue("additional_data", additionalDataForForm);
    methods.setValue("id", id);
    setActionNameModalOpen(open);
  };
  const nodesWithDeleteHandler = action
    .filter((node) => node.actionChainItem?.metadata.submitMethod !== "delete")
    .map((node) => ({
      ...node,
      type: "withDeleteHandler",
      selectable: true,

      data: {
        ...node.data,
        onDelete: handleDeleteNodes,
        onOpenModal: handleModalOpenOnNodeEdit,
        copyPasteItem: copyAndPasteNodes,
        previous_items_and: node.actionChainItem.previous_items_and,
        previous_items_or: node.actionChainItem.previous_items_or,
      },
    }));

  const connectionWithDeleteHandler = connection.map((con) => ({
    ...con,
    type: "withDeleteHandler",
    markerEnd: {
      type: MarkerType.ArrowClosed,
    },
    data: {
      ...con.data,
      onDelete: onDeleteConnection,
    },
  }));

  const sanatizeValues = (values: any) => {
    const editedJSON = values;

    if (typeof editedJSON === "string") {
      try {
        const editedObject = JSON.parse(editedJSON);
        return editedObject;
      } catch {
        const additionalDataError = {
          type: "validate",
          message: t("invalid_json_error"),
        };
        methods.setError("additional_data", additionalDataError);
        focusFirstInvalidField({ additional_data: additionalDataError });
        return undefined;
      }
    } else return editedJSON;
  };

  const addAction = (data: any) => {
    const NODE_SPACING = 150;
    const newUuId = uuidv4();

    const sanatizedAdditionalData = stripDefaultCreateFor(
      data.action.value,
      sanatizeValues(data.additional_data)
    );

    if (sanatizedAdditionalData === undefined) {
      return;
    }

    const additionalDataForItemTemplate: Partial<ActionChainItem> = {
      name: data.name,
      action: data.action.value,
      additional_data: sanatizedAdditionalData,
      action_chain_template: actionChainId,
      previous_items_and: [],
      previous_items_or: [],
      metadata: {
        position: positionOnAddingAction,
        submitMethod: "post",
      },
    };

    const newNode = {
      id: newUuId,
      position: positionOnAddingAction,

      data: {
        id: newUuId,
        label: data.name,
        action: data.action.value,
        additional_data: sanatizedAdditionalData,
      },
      actionChainItem: additionalDataForItemTemplate,
    };
    setLastActionNodeY(lastActionNodeY + NODE_SPACING);
    setAction((prevNodes: any) => [...prevNodes, newNode]);

    methods.reset();
    closeModal();
  };
  const editAction = (data: any) => {
    const idToEdit = data.id;

    const sanatizedAdditionalData = stripDefaultCreateFor(
      data.action.value,
      sanatizeValues(data.additional_data)
    );

    if (sanatizedAdditionalData === undefined) {
      return;
    }

    const tempSlicedArray = action.map((item: any) =>
      item.id === idToEdit
        ? {
            ...item,
            data: {
              ...item.data,
              label: data.name,
              action: data.action.value,
              additional_data: sanatizedAdditionalData,
            },
            actionChainItem: {
              ...item.actionChainItem,
              name: data.name,
              action: data.action.value,
              additional_data: sanatizedAdditionalData,
            },
          }
        : item
    );
    setAction(tempSlicedArray);

    setActionNameModalOpen(false);
  };

  const handleCreateAndEditNode = (data: any) => {
    if (modalMode === "create") {
      addAction(data);
    } else {
      editAction(data);
    }
  };

  const nodeTypes = useMemo(
    () => ({
      withDeleteHandler: (props: any) => (
        <ActionChainCustomNode
          {...props}
          nodeWidth={nodesWithDeleteHandlerWidth}
          nodeHeight={nodesWithDeleteHandlerHeight}
        />
      ),
      companyProgress: (props: any) => (
        <ActionChainCustomNode
          {...props}
          nodeWidth={companyProgressNodeWidth}
          nodeHeight={companyProgressNodeHeight}
          fontSize="12px"
        />
      ),
    }),
    []
  );

  const edgeTypes = useMemo(
    () => ({
      withDeleteHandler: (props: any) => (
        <ActionChainCustomConnection {...props} />
      ),
      companyProgress: (props: any) => (
        <ActionChainCustomConnection {...props} />
      ),
    }),
    []
  );

  const handleNavigateBack = () => {
    navigate("/settings/actionChains");
  };
  const resetStateOnCloseNotification = () => {
    setSuccessForNotification(false);
  };

  const onSelectionChange = ({ nodes, edges }: any) => {
    setSelectedNodes(nodes);
    setSelection(nodes.length > 0 || edges.length > 0);
  };

  return (
    <>
      <div style={{ width: "100%", height: "90%" }}>
        <div className={styles.heading_wrapper}>
          <Tooltip placement="right" title={t("back")}>
            <GenericButton
              variant="icon"
              color="iconBase"
              className={styles.backButton}
              onClick={handleNavigateBack}
            >
              <ArrowBackIcon />
            </GenericButton>
          </Tooltip>
        </div>
        <div className={styles.topActionBar}>
          <ActionChainActions
            openModal={openModal}
            batchDeleteActions={handleDeleteNodes}
            copyNodes={copyAndPasteNodes}
          />

          <GenericButton
            disabled={!hasUnsavedChanges}
            onClick={() => {
              const entryPointItem = action.find(
                (item) => item.actionChainItem.is_entrypoint === true
              );
              const isValid = entryPointItem
                ? action.some((item) =>
                    item.actionChainItem.previous_items_and?.some(
                      (prevItem: { id: string }) =>
                        prevItem.id === entryPointItem.id
                    )
                  )
                : false;

              if (!isValid) {
                handleError(t("actionchain_startpoint_no_connection"));
                return;
              }
              batchSubmitOfActionChainItems(
                action,
                removeDeletedNodesFromState,
                setRefreshtrigger,
                handleError,
                setNotificationVariant,
                setNotificationMessage,
                setSuccessForNotification,
                t
              );
            }}
          >
            {t("save")}
          </GenericButton>
        </div>
        <ReactFlow
          onPaneClick={openModal}
          onSelectionChange={onSelectionChange}
          nodes={nodesWithDeleteHandler}
          nodeTypes={nodeTypes}
          edges={connectionWithDeleteHandler}
          edgeTypes={edgeTypes}
          onNodesChange={onActionChange}
          onEdgesChange={onConnectionChange}
          onConnect={onAddConnection}
          onConnectStart={onConnectStart}
          onConnectEnd={onConnectEnd}
          snapToGrid={true}
          snapGrid={[15, 15]}
          fitView
          selectionMode={SelectionMode.Partial}
          multiSelectionKeyCode="Shift"
          elementsSelectable={true}
          nodesDraggable={true}
        >
          <Background variant={BackgroundVariant.Lines} gap={15} size={1} />
          <Controls />
          <MiniMap />
        </ReactFlow>
      </div>
      <Dialog open={actionNameModalOpen} fullWidth>
        <DialogTitle textAlign="center">{t("create")}</DialogTitle>

        <DialogContent sx={{ overflowY: "visible", width: "100%" }}>
          <FormProvider {...methods}>
            <form
              onSubmit={methods.handleSubmit(
                handleCreateAndEditNode,
                (errors) => {
                  focusFirstInvalidField(errors);
                },
              )}
              noValidate
            >
              <ActionChainModalContent
                availableActions={actionOptions}
                registerValidationTarget={registerValidationTarget}
              />
              <DialogActions
                sx={{ p: "0.75rem", justifyContent: "space-between" }}
              >
                <GenericButton variant="outlined" onClick={closeModal}>
                  {t("cancel")}{" "}
                </GenericButton>
                <GenericButton type="submit">
                  {modalMode === "create" ? t("create") : t("save")}
                </GenericButton>
              </DialogActions>
            </form>
          </FormProvider>
        </DialogContent>
      </Dialog>
      {errorModal}

      <GenericNotification
        message={successForNotification ? notificationMessage : null}
        variant={notificationVariant}
        handleCloseSnackbar={resetStateOnCloseNotification}
      />
    </>
  );
};

export default ActionChainCreateAndEdit;
// _____________________________________________________________________________________________
function getActionChainTemplateItemsById(
  actionChainTemplateId: string,
  actionState: any,
  setAction: any,
  setConnection: any,
  setLastActionNodeY: any,
  handleError: any,
  onLoaded?: (nodes: CustomNode[], edges: CustomEdge[]) => void
) {
  const additionnal: any = {};
  additionnal["action_chain_template"] = actionChainTemplateId;
  api.genericApiRequest({
    method: "get",
    entity: "actionChainItemTemplate",
    parametersToRender: { additionalUrlParameters: additionnal },
    successHandler: (res: any) => {
      const data = res.data.results;
      if (!res.data.results || res.data.results.length === 0) {
        // No persisted items (e.g. after deleting every node and saving):
        // clear the canvas and treat the empty graph as the saved baseline.
        setAction([]);
        setConnection([]);
        onLoaded?.([], []);
        return;
      }
      const tempActionStateData = transfromResponseToActionState(data);
      const tempConnectionStateData = transformResponseToConnectionState(data);

      const maxY = tempActionStateData?.reduce((max: any, item: any) => {
        const currentY = item.actionChainItem.metadata.position.y;
        return currentY > max ? currentY : max;
      }, -Infinity);

      setAction(tempActionStateData);
      setConnection(tempConnectionStateData);
      setLastActionNodeY(maxY);
      onLoaded?.(tempActionStateData, tempConnectionStateData);
    },
    failHandler: (error: any) => {
      handleError(error);
    },
  });
}

async function batchSubmitOfActionChainItems(
  items: any,
  removeDeletedNodesFromState: any,
  setRefreshtrigger: any,
  handleError: (error: any) => void,
  setNotificationVariant: any,
  setNotificationMessage: any,
  setShowNotification: any,
  t: any
) {
  if (!Array.isArray(items)) {
    return;
  }
  const config = {
    headers: {
      "Content-Type": "application/json",
    },
  };
  const itemsToPut = items.filter(
    (item: any) => item.actionChainItem.metadata.submitMethod === "put"
  );
  const itemsToPost = items.filter(
    (item: any) => item.actionChainItem.metadata.submitMethod === "post"
  );
  const itemsToDelete = items.filter(
    (item: any) => item.actionChainItem.metadata.submitMethod === "delete"
  );

  const preparedItemsToPost = itemsToPost.map((item: any) => {
    const actionChainItem = { ...item.actionChainItem };
    delete actionChainItem.previous_items_and;
    return {
      ...actionChainItem,
      id: item.id,
      metadata: {
        ...actionChainItem.metadata,
        position: item.position,
      },
    };
  });

  const preparedItemsToPut = itemsToPut.map((item: any) => ({
    ...item.actionChainItem,
    previous_items_and:
      item.actionChainItem.previous_items_and?.map((obj: any) => obj.id) || [],

    id: item.id,
    metadata: {
      ...item.actionChainItem.metadata,
      position: item.position,
    },
  }));

  const preparedItemsToDelete = itemsToDelete.map((item: any) => ({
    id: item.id,
  }));
  const postRequest = () =>
    new Promise((resolve) => {
      api.genericApiRequest({
        method: "post",
        entity: "actionChainItemTemplate",
        submitData: preparedItemsToPost,
        config: config,
        successHandler: (res: any) =>
          resolve({ success: true, data: res.data }),
        failHandler: (error: any) => resolve({ success: false, error }),
      });
    });
  const putRequest = (submitData: any) =>
    new Promise((resolve) => {
      api.genericApiRequest({
        method: "put",
        entity: "actionChainItemTemplate",
        submitData: submitData,
        config: config,
        successHandler: (res: any) =>
          resolve({ success: true, data: res.data }),
        failHandler: (error: any) => resolve({ success: false, error }),
      });
    });
  const deleteRequest = (submitData: any) =>
    new Promise((resolve) => {
      api.genericApiRequest({
        method: "delete",
        entity: "actionChainItemTemplate",
        entityId: submitData,
        successHandler: (res: any) =>
          resolve({ success: true, data: res.data }),
        failHandler: (error: any) => resolve({ success: false, error }),
      });
    });

  // ---DELETERequestHandling until BatchSupport was implemented--
  let deleteResult: any;
  const processDeletions = async (itemsToDelete: any[]) => {
    const deletedIds: any[] = [];
    for (const item of itemsToDelete) {
      deleteResult = await deleteRequest(item.id);
      if (deleteResult.success) {
        deletedIds.push(item.id);
      }
    }
    return deletedIds;
  };

  try {
    // ---POSTRequestHandling---
    const postResult: any =
      preparedItemsToPost.length > 0 ? await postRequest() : null;
    if (postResult && !postResult.success) {
      handleError(postResult.error);
      return;
    }
    // ---DELETERequesthandling--
    const deletedNodeIds = await processDeletions(preparedItemsToDelete);
    if (deletedNodeIds.length > 0) {
      removeDeletedNodesFromState(deletedNodeIds);
    }
    // ---sanatize PUTItems after DELETE---
    const sanitizedItemsToPut = preparedItemsToPut.map((item: any) => ({
      ...item,
      previous_items_and: item.previous_items_and.filter(
        (id: any) => !deletedNodeIds.includes(id)
      ),
    }));
    const finalPutItems = [
      ...sanitizedItemsToPut,
      ...itemsToPost
        .filter(
          (item: any) => item.actionChainItem.previous_items_and?.length > 0
        )
        .map((item: any) => ({
          ...item.actionChainItem,
          previous_items_and:
            item.actionChainItem.previous_items_and?.map(
              (obj: any) => obj.id
            ) || [],
          id: item.id,
          metadata: {
            ...item.actionChainItem.metadata,
            position: item.position,
          },
        })),
    ];
    let putResult: any;
    if (finalPutItems.length > 0) {
      putResult = await putRequest(finalPutItems);
      if (putResult && !putResult.success) {
        handleError(putResult.error);
      }
    }
    const results = [postResult, putResult, deleteResult].filter(Boolean);
    const allSuccessful = results.every((res) => res.success);
    if (results.length > 0 && allSuccessful) {
      setNotificationVariant("success");
      setNotificationMessage(t("save_successful"));
      setShowNotification(true);
    } else {
      setNotificationVariant("error");
      setNotificationMessage(t("error_occurred"));
      setShowNotification(true);
    }
    setRefreshtrigger((prev: any) => !prev);
  } catch (error) {
    console.error("ERROR", error);
  }
}
function getActionChain(
  actionChainId: string,
  handleSetActionChainTemplateName: any,
  handleError: any
) {
  api.genericApiRequest({
    method: "get",
    entity: "actionChainTemplate",
    entityId: actionChainId,

    successHandler: (res: any) => {
      handleSetActionChainTemplateName(res.data.name);
    },
    failHandler: (error: any) => {
      handleError(error);
    },
  });
}

export function transfromResponseToActionState(data: any) {
  let currentY = 0;
  return data.map((dataItem: any) => {
    const defaultX = 400;
    const defaultY = currentY;

    currentY += 150;

    return {
      id: dataItem.id,
      position: {
        x: dataItem?.metadata?.position?.x ?? defaultX,
        y: dataItem?.metadata?.position?.y ?? defaultY,
      },

      data: {
        label: dataItem.name,
        action: dataItem.action,
        additional_data: dataItem.additional_data,
        id: dataItem.id,
        is_entrypoint: dataItem.is_entrypoint,
      },
      actionChainItem: {
        name: dataItem.name,
        action: dataItem.action,
        is_entrypoint: dataItem.is_entrypoint,
        additional_data: dataItem.additional_data,
        action_chain_template: dataItem.action_chain_template.id,
        metadata: {
          ...dataItem.metadata,
          submitMethod: "put",
          position: {
            x: dataItem?.metadata?.position?.x ?? defaultX,
            y: dataItem?.metadata?.position?.y ?? defaultY,
          },
        },

        submitMethod: "put",
        previous_items_and: dataItem.previous_items_and,
        previous_items_or: dataItem.previous_items_or,
      },
      measured: {
        width: 200,
        height: 46,
      },
    };
  });
}

export function transformResponseToConnectionState(response: any) {
  return response.reduce((edges: any, item: any) => {
    const targetId = item.id;
    const targetData = {
      action: item.action,
      additional_data: item.additional_data,
      id: item.id,
      label: item.name,
      status: "",
    };

    item.previous_items_and.forEach((previousItem: any) => {
      const sourceId = previousItem.id;

      const sourceItem = response.find((res: any) => res.id === sourceId);

      const sourceData = sourceItem
        ? {
            action: sourceItem.action,
            additional_data: sourceItem.additional_data,
            id: sourceItem.id,
            label: sourceItem.name,
          }
        : {};

      edges.push({
        target: targetId,
        source: sourceId,
        id: `xy-edge__${sourceId}-${targetId}`,
        type: "withDeleteHandler",
        markerEnd: { type: "arrowClosed" },
        data: {
          sourceData,
          targetData,
        },
      });
    });

    return edges;
  }, []);
}
